import { Timestamp } from "firebase/firestore";
import { VideoData } from "./types/Videos";
import html2canvas from "html2canvas";
import * as Sentry from '@sentry/browser';
import { trackHtml2CanvasError } from '~/utils/error-tracking'; // Import from error-tracking module
import { SDKData, VA } from "./utils/interface/sdkData";
import { Filters, Group } from "./components/Projects/ProjectAnalysis";

// Interface for html2canvas options
interface Html2CanvasOptions {
    scale?: number;
    useCORS?: boolean;
    backgroundColor?: string | null;
    [key: string]: any; // Allow other html2canvas options
}

interface ApiGroupData {
    projectId: string;
    name: string;
    color: string;
    measurability: string[];
    interestingEvents: { min: number; max: number };
    videoDurationInFrames: { min: number; max: number };
    customTags: { tagId: string; enabled: boolean }[];
  }
  

// Utility functions 
export const videosFormat = (
    videoObject: any,
) => {
    let newArray: VideoData[] = [];
    for (const video in videoObject) {
        const existingVideoIndex = newArray.findIndex((v) => v.id === video);

        if (existingVideoIndex === -1) {
            newArray = [...newArray, { id: video, data: videoObject[video] }];
        } else {
            newArray[existingVideoIndex] = {
                id: video,
                data: videoObject[video],
            };
        }
    }
    return newArray;
};

export const dateFormat = (
    date: string | number | Date | Timestamp | undefined
): string => {
    if (!date) return 'Invalid Date';

    const formattedDate = date instanceof Timestamp ? date.toDate() : new Date(date);

    return formattedDate.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
};

export const truncateText = (text: string, maxChars: number = 20) => {
    if (text.length > maxChars) {
        return text.slice(0, maxChars) + "...";
    }
    return text;
};

export const toCamelCase = (str: string) =>
    str
        .replace(/\s+(.)/g, (_, char) => char.toUpperCase())
        .replace(/^([A-Z])/, (match) => match.toLowerCase());

export const generateRandomString = (length: number): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
}

// Reusable function to export any DOM element as PNG


export const exportToPng = (
    element: HTMLElement | null,
    filename: string = 'download.png',
    options: Html2CanvasOptions = {}
): void => {
    const startTime = performance.now();

    try {
        if (!element) {
            const errorMessage = 'Element not found for PNG export';
            console.error(errorMessage);

            Sentry.captureMessage(errorMessage, {
                level: 'error',
                tags: {
                    operation: 'exportToPng',
                    status: 'error',
                    errorType: 'element_not_found',
                },
                extra: {
                    filename,
                    options,
                },
            });
            return;
        }

        // Create an off-screen container
        const offScreenContainer = document.createElement('div');
        offScreenContainer.style.position = 'absolute';
        offScreenContainer.style.left = '-9999px';
        offScreenContainer.style.top = '-9999px';
        offScreenContainer.style.visibility = 'hidden';
        document.body.appendChild(offScreenContainer);

        // Clone the element
        const clonedElement = element.cloneNode(true) as HTMLElement;

        // Apply styles to the cloned element to ensure full content is captured
        clonedElement.style.visibility = 'visible';
        clonedElement.style.position = 'static';
        clonedElement.style.overflow = 'visible';
        clonedElement.style.height = 'auto';
        clonedElement.style.width = 'auto';
        clonedElement.style.padding = element.style.padding;
        clonedElement.style.boxSizing = 'border-box';


        const metricsContainer = clonedElement.querySelector('.metrics-container') as HTMLElement;
        if (metricsContainer) {
            metricsContainer.style.maxHeight = 'none';
            metricsContainer.style.height = 'auto';
            metricsContainer.style.overflow = 'visible';
            metricsContainer.style.paddingBottom = element.style.paddingBottom || '0px';
        }

        // Append the cloned element to the off-screen container
        offScreenContainer.appendChild(clonedElement);

        // Calculate the full content size
        const width = clonedElement.scrollWidth;
        const height = clonedElement.scrollHeight;

        const defaultOptions: Html2CanvasOptions = {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff',
            width,
            height,
            windowWidth: width,
            windowHeight: height,
            scrollX: 0,
            scrollY: 0,
            ...options,
        };

        // Track rendering time separately
        const renderStartTime = performance.now();

        html2canvas(clonedElement, defaultOptions)
            .then((canvas: HTMLCanvasElement) => {
                // Clean up: remove the off-screen container
                document.body.removeChild(offScreenContainer);

                // Calculate rendering time
                const renderTime = performance.now() - renderStartTime;

                try {
                    // Start timing data URL conversion and download
                    const downloadPrepStartTime = performance.now();

                    // Convert canvas to data URL
                    const pngDataUrl: string = canvas.toDataURL('image/png');

                    // Estimate file size from data URL (approximate)
                    const base64Length = pngDataUrl.length - 'data:image/png;base64,'.length;
                    const fileSize = Math.ceil(base64Length * 0.75); // Approximate size in bytes

                    // Start timing download initiation
                    const downloadStartTime = performance.now();

                    // Create download link
                    const link: HTMLAnchorElement = document.createElement('a');
                    link.href = pngDataUrl;
                    link.download = filename;

                    // Calculate download prep time
                    const downloadPrepTime = downloadStartTime - downloadPrepStartTime;

                    // Create a promise that resolves when the download starts
                    const downloadPromise = new Promise<void>((resolve) => {
                        link.onclick = () => {
                            resolve();
                        };
                        setTimeout(() => {
                            resolve();
                        }, 100);
                    });

                    link.click();

                    // Clean up
                    URL.revokeObjectURL(link.href);

                    // Log performance metrics to Sentry after download initiated
                    downloadPromise.then(() => {
                        const totalTime = performance.now() - startTime;
                        const downloadInitTime = performance.now() - downloadStartTime;

                        Sentry.captureMessage('PNG Export Completed', {
                            level: 'info',
                            tags: {
                                operation: 'exportToPng',
                                status: 'success',
                                fileFormat: 'PNG',
                            },
                            extra: {
                                filename,
                                timing: {
                                    totalMs: totalTime,
                                    renderMs: renderTime,
                                    downloadPrepMs: downloadPrepTime,
                                    downloadInitiationMs: downloadInitTime,
                                },
                                canvasDimensions: {
                                    width: canvas.width,
                                    height: canvas.height,
                                },
                                contentDimensions: {
                                    width,
                                    height,
                                },
                                options: {
                                    scale: defaultOptions.scale,
                                    useCORS: defaultOptions.useCORS,
                                    backgroundColor: defaultOptions.backgroundColor,
                                },
                                approximateFileSizeBytes: fileSize,
                                approximateFileSizeKB: Math.round(fileSize / 1024),
                            },
                        });
                    });
                } catch (exportError) {
                    Sentry.captureException(exportError, {
                        tags: {
                            operation: 'exportToPng',
                            stage: 'dataUrl',
                            status: 'error',
                        },
                        extra: {
                            filename,
                            canvasDimensions: {
                                width: canvas.width,
                                height: canvas.height,
                            },
                            duration: performance.now() - startTime,
                        },
                    });

                    console.error(`Error in data URL generation or download for ${filename}:`, exportError);
                }
            })
            .catch((renderError: Error) => {
                // Clean up: remove the off-screen container on error
                document.body.removeChild(offScreenContainer);

                const errorDetails = trackHtml2CanvasError(renderError, clonedElement, defaultOptions);

                Sentry.captureException(renderError, {
                    tags: {
                        operation: 'exportToPng',
                        stage: 'html2canvas',
                        status: 'error',
                        errorType: errorDetails.errorType,
                    },
                    extra: {
                        filename,
                        errorDetails,
                        elementInfo: {
                            id: clonedElement.id,
                            className: clonedElement.className,
                            dimensions: {
                                width: clonedElement.offsetWidth,
                                height: clonedElement.offsetHeight,
                            },
                        },
                        options: defaultOptions,
                        duration: performance.now() - startTime,
                    },
                });

                console.error(`Error generating PNG for ${filename}:`, renderError);
            });
    } catch (unexpectedError) {
        Sentry.captureException(unexpectedError, {
            tags: {
                operation: 'exportToPng',
                stage: 'unexpected',
                status: 'error',
            },
            extra: {
                filename,
                elementInfo: element
                    ? {
                        id: element.id,
                        className: element.className,
                    }
                    : 'Element is null',
                options,
                duration: performance.now() - startTime,
            },
        });

        console.error(`Unexpected error in exportToPng for ${filename}:`, unexpectedError);
    }
};


//this exports without any extra settings or manipulation
export const naturalExportToPng = async (
  element: HTMLElement | null,
  filename: string = 'download.png',
  options: Html2CanvasOptions = {}
): Promise<void> => {
  if (!element) {
    console.error('❌ Element not found for PNG export');
    return;
  }

  try {
    // Wait for fonts and images to fully load
    await document.fonts.ready;
    await Promise.all(
      Array.from(element.querySelectorAll('img')).map((img) => {
        if (img.complete) return Promise.resolve();
        return new Promise((res) => {
          img.onload = img.onerror = res;
        });
      })
    );

    // Get element dimensions
    const rect = element.getBoundingClientRect();

    // Configure html2canvas options
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      scrollX: 0,
      scrollY: -window.scrollY,
      width: rect.width,
      height: rect.height,
      ...options,
    });

    // Convert to PNG data URL
    const pngDataUrl = canvas.toDataURL('image/png');

    // Trigger download
    const link = document.createElement('a');
    link.href = pngDataUrl;
    link.download = filename;
    link.click();

    console.log(`✅ Exported ${filename} successfully`);
  } catch (error) {
    console.error('❌ Error exporting PNG:', error);
  }
};




/**
 * Determines if a VA point is valid based on the schema and video health data.
 * @param sdkData - The SDK data containing VA points and video health.
 * @param vaPoint - The VA point to check.
 * @param frame - The frame number of the VA point.
 * @returns True if the VA point is valid, false otherwise.
 */
export function isVAPointValid(sdkData: SDKData, vaPoint: VA, frame: number): boolean {
    if (!sdkData || !vaPoint) return false;
    if (sdkData?.sv) {
        return (
            vaPoint.valid !== false &&
            (vaPoint.valence !== 0 || vaPoint.arousal !== 0)
        );
    } else {
        return !sdkData.video_health?.affect_invalid?.includes(frame);
    }
}



/**
 * Computes adjusted Valence-Arousal (VA) points by optionally subtracting the typical average valence and arousal.
 * @param sdkData - The SDK data containing VA points and typical event data.
 * @param applyBaseline - Whether to apply the baseline adjustment (default: false).
 * @returns An array of VA points with adjusted valence and arousal values for valid points if applyBaseline is true.
 */
export function computeAdjustedVAPoints(
    sdkData: SDKData,
    applyBaseline: boolean = false
): VA[] {
    if (!sdkData || !Array.isArray(sdkData.va)) {
        return [];
    }
    if (!applyBaseline) {
        return sdkData.va;
    }
    const typicalEvent = sdkData.interesting_events?.typical;
    const typicalValence = typicalEvent?.average_valence ?? 0;
    const typicalArousal = typicalEvent?.average_arousal ?? 0;
    return sdkData.va.map((vaPoint) => {
        if (isVAPointValid(sdkData, vaPoint, vaPoint.frame)) {
            return {
                ...vaPoint,
                valence: vaPoint.valence - typicalValence,
                arousal: vaPoint.arousal - typicalArousal,
            };
        }
        return vaPoint;
    });
}


export function normalizeEmail(input: string): string {
    return input.trim().toLowerCase();
  }


  export const restructureGroupForApi = (group: Group, projectId: string): ApiGroupData => {
    return {
      projectId,
      name: group.name,
      color: group.color,
      measurability: Object.keys(group.filters.measurability).filter(
        (key) => group.filters.measurability[key as keyof typeof group.filters.measurability]
      ),
      interestingEvents: {
        min: Math.round(group.filters.minInterestingEvents),
        max: Math.round(group.filters.maxInterestingEvents),
      },
      videoDurationInFrames: {
        min: Math.round(group.filters.minDuration),
        max: Math.round(group.filters.maxDuration),
      },
      customTags: Object.entries(group.filters.customTags).map(([tagId, enabled]) => ({
        tagId,
        enabled,
      })),
    };
  };



  export const transformToGroup = (raw:any, allTags:any): Group => {
    const {
      id,
      name,
      color,
      measurability,
      interestingEvents,
      videoDurationInFrames,
      customTags,
    } = raw;
  
    const filters: Filters = {
      measurability: {
        Low: measurability.includes('Low'),
        Medium: measurability.includes('Medium'),
        High: measurability.includes('High'),
      },
      minInterestingEvents: interestingEvents.min,
      maxInterestingEvents: interestingEvents.max,
      minDuration: videoDurationInFrames.min,
      maxDuration: videoDurationInFrames.max,
      customTags: allTags.reduce((acc:any, tag:any) => {
        acc[tag.id] = customTags ? !!customTags[tag.id] : false;
        return acc;
      }, {} as { [key: string]: boolean }),
    };
  
    return {
      id,
      name,
      color,
      filters,
      applyBaseline: false,
    };
  };
  
  
  